/**
 * Streak labelling in the habit's own period unit.
 *
 * Backend streaks are counted in recurrence *periods*, not days
 * (`_compute_streaks` advances once per period), so rendering a bare number
 * next to the word "day" is wrong for every non-daily habit: a habit touched
 * once every 46 days that has been completed twice in a row has a 2-cycle
 * streak spanning ~92 days, not a "2-day streak".
 *
 * Lives here rather than in either page so Dashboard and HabitStats cannot
 * drift apart, and so the metrics glossary can describe one implementation.
 */

export type RecurrenceType = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';

const BASE_UNIT: Record<string, string> = {
  daily: 'day',
  weekly: 'week',
  monthly: 'month',
  yearly: 'year',
  // A custom recurrence counts in days, so interval 1 reads "days" and any
  // larger interval becomes an N-day cycle.
  custom: 'day',
};

const plural = (word: string, count: number): string =>
  count === 1 ? word : `${word}s`;

/**
 * The noun for one unit of this habit's streak — "days", "weeks",
 * "46-day cycles". Use for axis/caption labels where the count is shown
 * separately.
 */
export const streakUnitNoun = (
  recurrenceType?: RecurrenceType | string | null,
  interval: number = 1,
  count: number = 2,
): string => {
  const base = BASE_UNIT[recurrenceType || 'daily'] || 'day';
  if (!interval || interval <= 1) {
    return plural(base, count);
  }
  return plural(`${interval}-${base} cycle`, count);
};

/**
 * A complete streak label: "12 days", "5 weeks", "2 × 46-day cycles".
 *
 * Falls back to a bare count when the recurrence is unknown (an older API
 * response), which is still better than asserting the wrong unit.
 */
export const formatStreak = (
  count: number,
  recurrenceType?: RecurrenceType | string | null,
  interval: number = 1,
): string => {
  if (!recurrenceType) {
    return String(count);
  }
  const base = BASE_UNIT[recurrenceType] || 'day';
  if (!interval || interval <= 1) {
    return `${count} ${plural(base, count)}`;
  }
  // Multi-interval recurrences read as a multiplication so the span is legible:
  // "2 × 46-day cycles" makes the ~92 days of adherence obvious.
  return `${count} × ${plural(`${interval}-${base} cycle`, count)}`;
};

/** The habit's period as a singular noun: "day", "week", "46-day cycle". */
export const periodNoun = (
  recurrenceType?: RecurrenceType | string | null,
  interval: number = 1,
): string => {
  const base = BASE_UNIT[recurrenceType || 'daily'] || 'day';
  return !interval || interval <= 1 ? base : `${interval}-${base} cycle`;
};
